import {
  Component,
  OnInit,
  Input,
  ElementRef,
  ViewChild,
  OnDestroy,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { pairwise, filter, debounce, switchMap, takeUntil, startWith } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  Project,
  ProjectManagerService,
  CommunicationManagerService,
  ModalService,
  OpsVersion,
  DataSyncService,
} from '@ops/shared-types';
import { interval, Subject, Subscription } from 'rxjs';
import { LocalStorageService } from '../../../../shared-types/src/lib/services/storage.service';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { ActionMenuOption } from '../action-menu/action-menu.component';

export interface VersionGenesis {
  versionName?: string;
  cloneMedia?: boolean;
  cloneTeam?: boolean;
  cloneLogistics?: boolean;
}

export interface VersioningElement {
  title: string;
  isSelected?: boolean;
}

export interface SelectionItem {
  label: string;
  isSelected?: boolean;
}

@Component({
  selector: 'app-version-menu',
  templateUrl: './version-menu.component.html',
  styleUrls: ['./version-menu.component.scss'],
})
export class VersionMenuComponent implements OnInit, OnDestroy {
  @Input('projectId') set setProjectId(projectId: any) {
    this.projectId = projectId ?? '';
  }
  versions$: any;

  project$: any;

  @ViewChild('menuOpen') menuOpen: ElementRef;
  @ViewChild('menuClosed') menuClosed: ElementRef;
  @ViewChild('background') menuBackground: ElementRef;
  @ViewChildren('versionActionMenu') versionActionMenus: QueryList<ElementRef>;

  project: Project;

  projectId: string;
  selectedProjectId: string;

  isOpen: boolean;
  _createView: boolean = false;
  mediaOpen: boolean;
  teamOpen: boolean;

  renameId: string;
  renameInput: string;
  approvedProjectId: string;

  versionList: Array<OpsVersion>;
  newVersion: VersionGenesis;
  projectList: Array<Project>;
  mediaTypeSelection: Array<SelectionItem>;
  teamAndAssemblySelection: Array<SelectionItem>;

  selectionElements: Array<VersioningElement> = [];
  isAllToggled: boolean = false;

  //TODO: change BE endpoint to take on project elements
  mediaOpsEnums: Array<string>;
  teamOpsEnums: Array<string>;
  rentalsOpsEnums: Array<string>;

  approvedErrorMessage: string;

  searchParam: string;
  displayedProjects: Array<Project>;

  subscriptions: Array<Subscription> = [];

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private pm: ProjectManagerService,
    private cm: CommunicationManagerService,
    private modalService: ModalService,
    private localStorageService: LocalStorageService,
    private dataSyncService: DataSyncService,
    private translateService: TranslateService
  ) {}

  set createView(val: boolean) {
    this._createView = val;
  }

  get createView(): boolean {
    return this._createView;
  }

  ngOnInit(): void {
    this.setData();

    this.createView = false;

    this.selectionElements.push(
      {
        title: this.translateService.instant('VERSION_MENU.DELIVERABLES'),
        isSelected: false,
      },
      {
        title: this.translateService.instant('VERSION_MENU.TEAM_AND_LOGISTICS'),
        isSelected: false,
      }
    );
    const sub = this.pm
      .getAllProjects(this.localStorageService.getCurrentCompany()?._id)
      .subscribe((projects: Array<Project>) => {
        this.projectList = projects;
      });
    this.subscriptions.push(sub);

    if (!this.project) {
      const currentProjectFromStorage =
        this.localStorageService.getCurrentProject();
      this.project = currentProjectFromStorage?._id
        ? currentProjectFromStorage
        : {
            _id: '0',
            name: this.translateService.instant('VERSION_MENU.LOADING_PROJECT'),
          };
    }
  }

  ngOnDestroy(): void {
    this.createView = false;
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.project$?.unsubscribe();
    this.versions$?.unsubscribe();
    this.subCPSelVer?.unsubscribe();
    this.subCPSelProj?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setData() {
    this.project$ = this.pm
      .getCurrentPojectObservable()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, curr]) => {
          // Only process if current is different from previous
          if (!curr) return false;
          if (!prev) return true;
          return prev._id !== curr._id || 
                 prev.status !== curr.status;
        }),
        debounce(() => interval(500)),
        takeUntil(this.destroy$)
      )
      .subscribe(([prev, project]: [Project | null, Project]) => {
        // console.log('NEXTED ', project.versionName);
        // console.log('prev project',  project);
        // if (this.projectId === project._id) return;
        if (!project) {
          return;
        }

        this.projectId = project._id;
        const previousVersionLink = this.project?.versionLink ?? '';
        this.project = {
          _id: project._id,
          client: {
            _id: project.client?._id ?? '',
            name: project.client?.name ?? '',
          },
          name: project.name,
          code: project.code,
          company: project.company,
          versionLink: project.versionLink,
        };

        // if (this.project.versionLink === previousVersionLink) {
        //   // console.log('skipped ', this.project.name);
        //   return;
        // }

        this.versions$ = this.pm
          .getProjectVersioningInfo(this.pm.getCurrentProject()?._id ?? '')
          .subscribe((ProjectVersioningInfo) => {
            this.versionList = [];
            this.versionList = ProjectVersioningInfo['versions'];
            this.versionList = this.versionList.sort((vb: any, va: any) => {
              return (
                new Date(vb.createdAt).getTime() -
                new Date(va.createdAt).getTime()
              );
            });
            let approved = this.versionList.find((v: any) => v.isApproved);
            this.approvedProjectId = '';
            if (approved) {
              this.project._id = approved._id ?? '';
              this.approvedProjectId = approved._id ?? '';
            }

            setTimeout(() => {
              this.projectList = this.projectList?.filter(
                (p) => p.company === this.project.company
              );
              this.displayedProjects = this.projectList;
            }, 1000);
          });
      });
  }

  onToggleOpen() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.createView = false;
    // this.menuBackground.nativeElement.style.display = 'flex';
    this.isOpen = true;
  }

  close() {
    this.createView = false;
    // this.menuBackground.nativeElement.style.display = 'none';
    this.isOpen = false;
  }

  openModal(id: string) {
    //    console.log('OPEN', id);
    this.modalService.open(id);
  }
  closeModal(id: string) {
    this.modalService.close(id);
  }




  toggleModal(id: string) {
    this.modalService.toggle(id);
  }



  onSelectAction(selection: string, estimateId: string, versionName: string) {
    const newLocal = 'VERSION_MENU.DELETE';
    //TODO: pass version object here to simplify

    this.dataSyncService.refetchVersionListener$.next(
      new Date().getTime()
    );

    switch (selection) {
      case 'VERSION_MENU.RENAME': {
        this.renameId = estimateId;
        this.renameInput = versionName;
        setTimeout(
          () => document.getElementById('input' + estimateId)?.focus(),
          10
        );
        return;
      }
      case 'VERSION_MENU.DUPLICATE': {
        //TODO check when BE updated
        let info = {
          versionName: versionName + ' copy',
          cloneMedia: true,
          cloneTeam: true,
          cloneLogistics: true,
        };
        const sub = this.pm
          .newVersionClone(info, estimateId, this.project.company)
          .subscribe((projectId) => {
            this.versionList.push({
              _id: projectId,
              name: info.versionName,
              createdAt: new Date(),
            });
          });
        this.subscriptions.push(sub);
        return;
      }
      case 'VERSION_MENU.APPROVED_BY_CLIENT': {
        const sub = this.cm
          .put('project/approve', null, [
            { headerName: 'projectId', headerValue: estimateId },
          ])
          .subscribe((project) => {
            this.approvedProjectId = estimateId;
            this.versionList.forEach((v) =>
              v._id === estimateId
                ? (v.isApproved = true)
                : (v.isApproved = false)
            );
            this.pm
              .getAllProjects(this.project.company)
              .subscribe((projects: Array<Project>) => {
                this.projectList = projects.sort((a: any, b: any) =>
                  b.code.localeCompare(a.code)
                );
              });
            this.dataSyncService.refetchVersionListener$.next(
              new Date().getTime()
            );
          });
        this.subscriptions.push(sub);
        return;
      }
      case 'VERSION_MENU.REMOVE_APPROVAL':
      case 'VERSION_MENU.ADDITIONALLY_APPROVED': {
        const sub = this.pm.addEstimateToApproved(estimateId).subscribe(() => {
          //TODO simplify
          const versionItem = this.versionList?.find(
            (v) => v._id === estimateId
          );

          if (versionItem)
            versionItem.isAddedToApproved = !versionItem?.isAddedToApproved;

          this.dataSyncService.refetchVersionListener$.next(
            new Date().getTime()
          );
        });
        this.subscriptions.push(sub);
        return;
      }
      case newLocal: {
        if (
          confirm(
            this.translateService.instant('windowConfirm.versionMenu.deleteProjectVersion', { versionName })
          )
        ) {
          const sub = this.cm
            .delete('project/version', [
              { headerName: 'projectId', headerValue: estimateId },
            ])
            .subscribe(() => {
              this.versionList = this.versionList.filter(
                (p) => p._id != estimateId
              );

              this.modalService.closeAll();
              this.localStorageService.deleteProject(estimateId);

              if (this.isSelectedVersion({ _id: estimateId })) {
                if (this.versionList.length) {
                  const nextToSelect = this.versionList.filter(
                    (vi) => vi.isApproved
                  );

                  if (nextToSelect.length) {
                    this.onSelectVersion(nextToSelect[0]);
                  } else if (this.versionList.length) {
                    this.onSelectVersion(this.versionList[0]);
                  } else {
                    setTimeout(() => {
                      this.localStorageService.clearAndFetchForRecalculation();
                      this.router.navigate(['/dashboard']);
                      this.close();
                    }, 150);
                  }
                } else {
                  setTimeout(() => {
                    this.localStorageService.clearAndFetchForRecalculation();
                    this.router.navigate(['/dashboard']);
                    this.close();
                  }, 150);
                }
              }
              // this.createView = fa;

              // setTimeout(() => {
              //   this.localStorageService.clearAndFetchForRecalculation();
              //   this.router.navigate(['/dashboard']);
              //   this.close();
              // }, 150);
            });
          this.subscriptions.push(sub);
        }
        return;
      }
      default:
        break;
    }
    this.closeModal('version-action-menu-' + estimateId);
  }

  onRenameKeyUp(event: any): void {
    if (event.key === 'Enter' || event.keyCode === 13) {
      const sub = this.pm
        .updateProject(this.renameId, { versionName: this.renameInput })
        .subscribe(() => {
          const versionItem = this.versionList?.find(
            (v) => v._id === this.renameId
          );

          if (versionItem) versionItem.name = this.renameInput;
          this.renameId = '';
        });
      this.subscriptions.push(sub);
      return;
    }
    this.renameInput = event.target.value;
  }

  cancelRename() {
    this.renameId = '';
  }

  subCPSelVer?: Subscription;
  subCPSelProj?: Subscription;

  onSelectVersion(guid?: OpsVersion) {
    this.dataSyncService.refetchVersionListener$.next(
      new Date().getTime()
    );
    
    this.close();
    if (this.subCPSelVer) {
      this.subCPSelVer?.unsubscribe();
    }

    this.subCPSelVer = this.pm
      .getProjectById(guid._id)
      .subscribe((project: Project) => {
        const isM2 = this.pm.getCurrentProject().isReal;
        this.pm.setCurrentProject(project);
        this.close();
        if (isM2) {
          this.router.navigate(['project-estimate/project-info']);
        }
      });
  }

  onSelectProject(guid: string) {
    this.close();
    // this.router.navigate(['dashboard']);
    if (this.subCPSelProj) {
      this.subCPSelProj?.unsubscribe();
    }

    this.subCPSelProj = this.pm
      .getProjectById(guid)
      .subscribe((project: Project) => {
        // console.log('navigation return selected project on version click');
        this.pm.setCurrentProject(project);

        if (this.router.url.indexOf('production-cost') > -1) {
          this.router.navigate(['/production-cost', project._id]);
          // window.location.reload();
        }

        this.close();
      });
    // this.subscriptions.push(sub);
  }

  onSelectCost() {
    if (this.approvedProjectId) {
      this.router.navigate(['production-cost', this.approvedProjectId]);
      this.close();
      return;
    }
    this.router.navigate(['production-cost', this.pm.getCurrentProject()?._id]);
    this.close();
  }

  onCreateNew() {
    this.createView = true;
    this.newVersion = {};
  }

  onInputName(event: any) {
    this.newVersion.versionName = event.target.value;
  }

  onToggleElement(category: VersioningElement) {
    category.isSelected = !category.isSelected;
  }

  onCancel() {
    this.newVersion = {};
    this.createView = false;
  }

  onSave() {
    this.selectionElements.forEach((category) => {
      if (category.title?.toUpperCase().startsWith('M')) {
        this.newVersion.cloneMedia = category.isSelected;
      }
      if (category.title?.toUpperCase().startsWith('T')) {
        this.newVersion.cloneTeam = category.isSelected;
        this.newVersion.cloneLogistics = category.isSelected;
      }
    });
    const sub = this.pm
      .newVersionClone(this.newVersion, this.project._id, this.project.company)
      .pipe(
        switchMap((projectId) => {
          this.versionList.push({
            _id: projectId,
            name: this.newVersion.versionName,
            createdAt: new Date(),
          });
          this.versionList = this.versionList.sort((va: any, vb: any) => {
            return (
              new Date(va.createdAt).getTime() -
              new Date(vb.createdAt).getTime()
            );
          });
          return this.pm.getProjectVersioningInfo(projectId);
        })
      )
      .subscribe((projectVersioningInfo) => {
        setTimeout(() => {
          this.createView = false;
        }, 10);
      });
    this.subscriptions.push(sub);
  }

  onTypeSearch(event: any) {
    this.searchParam = event.target.value?.toUpperCase();
    if (this.searchParam.length > 1) {
      this.displayedProjects = this.projectList.filter(
        (p) =>
          p.name?.toUpperCase().includes(this.searchParam) ||
          p.client?.name?.toUpperCase().includes(this.searchParam) ||
          p.code?.toUpperCase().includes(this.searchParam)
      );
      return;
    }
    this.displayedProjects = this.projectList;
  }

  get projectCode(): string {
    return this.project.code ?? '';
  }

  get clientName(): string {
    return this.project.client?.name ?? '';
  }

  get projectName(): string {
    return this.project.name ?? '';
  }

  get newVersionLabel(): string {
    return 'V' + (this.versionList?.length! + 1);
  }

  get projectListMaxHeight(): string {
    return `calc(90vh - ${304 + this.versionList?.length! * 45}px)`;
  }

  get newVersionName() {
    return this.newVersion?.versionName || '';
  }

  set newVersionName(versionName: string) {
    if (!this.newVersion) {
      this.newVersion = {};
    }

    this.newVersion.versionName = versionName;
  }

  getOptions(version: any): ActionMenuOption[] {
    let options: ActionMenuOption[] = [];
    
    if (version.isApproved) {
      options = [
        { label: 'VERSION_MENU.DUPLICATE', value: 'VERSION_MENU.DUPLICATE' },
        { label: 'VERSION_MENU.RENAME', value: 'VERSION_MENU.RENAME' },
      ];
    } else if (version.isAddedToApproved) {
      options = [
        { label: 'VERSION_MENU.DUPLICATE', value: 'VERSION_MENU.DUPLICATE' },
        { label: 'VERSION_MENU.RENAME', value: 'VERSION_MENU.RENAME' },
        { label: 'VERSION_MENU.REMOVE_APPROVAL', value: 'VERSION_MENU.REMOVE_APPROVAL' },
      ];
    } else {
      options = [
        { label: 'VERSION_MENU.DUPLICATE', value: 'VERSION_MENU.DUPLICATE' },
        { label: 'VERSION_MENU.RENAME', value: 'VERSION_MENU.RENAME' },
        { label: 'VERSION_MENU.APPROVED_BY_CLIENT', value: 'VERSION_MENU.APPROVED_BY_CLIENT' },
        { label: 'VERSION_MENU.ADDITIONALLY_APPROVED', value: 'VERSION_MENU.ADDITIONALLY_APPROVED' },
        { label: 'VERSION_MENU.DELETE', value: 'VERSION_MENU.DELETE', isDelete: true },
      ];
    }

    // Remove delete option if it's the only version
    if (this.versionList.length === 1) {
      options = options.filter(option => option.value !== 'VERSION_MENU.DELETE');
    }

    return options;
  }

  getVersionLabel(index: number): string {
    return 'V' + (index + 1);
  }

  isSelectedVersion(version?: OpsVersion): boolean {
    if (!version || !version?._id) return false;
    return version._id === this.projectId;
  }

  versionId(version: OpsVersion) {
    return 'version-' + version._id;
  }
}
